import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type PendingChangeEntry } from '../lib/api';
import { ChannelTag, formatChangeValue } from './ChangeBits';

/**
 * Everything staged, waiting to be pushed together.
 *
 * Modelled closely on HistoryTab — old → new, on a channel, by a person — since
 * a staged change and a pushed one are the same fact at different points in
 * time. The difference is what a row can still do about itself: a pushed
 * change can only be reverted; a staged one can be cancelled, retargeted, or
 * pushed on its own before the rest of the queue goes.
 */

export function PendingChanges({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null);

  // A row's status/message only gets re-evaluated when a real batch runs
  // (which has side effects — it pushes whatever turns out fine) unless
  // something else asks first. recheckQueue is that something else: no
  // network call, never pushes, just brings status/conflictValue up to date
  // with what's true right now. Run once whenever this tab is opened, before
  // the queue below is even read, so nobody sees a row still labeled from
  // whenever it was last actually pushed. Read-only viewers can't act on any
  // of this anyway and the endpoint requires editor, so they skip straight
  // to reading the queue as it stands.
  const recheckMutation = useMutation({
    mutationFn: () => api.recheckQueue(),
    // A failed recheck (e.g. a transient network blip) still has to let the
    // query below run — showing yesterday's status is better than showing
    // nothing at all.
    onError: (err: Error) => setToast({ kind: 'warn', text: `Could not refresh staged changes: ${err.message}` }),
  });
  useEffect(() => {
    if (canEdit) recheckMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['queue'],
    queryFn: api.queue,
    enabled: !canEdit || recheckMutation.isSuccess || recheckMutation.isError,
    refetchInterval: (query) =>
      query.state.data?.entries.some((entry) => entry.status === 'executing') ? 2000 : false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['queue'] });
    void queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const executeMutation = useMutation({
    mutationFn: (ids?: string[]) => api.executeQueue(ids),
    onSuccess: (result) => {
      invalidate();
      const { executed, conflicts, needsConfirmation, failed } = result.summary;
      const needsReview = conflicts + needsConfirmation;
      setToast(
        needsReview + failed === 0
          ? { kind: 'ok', text: `${executed} change${executed === 1 ? '' : 's'} pushed and verified` }
          : {
              kind: failed > 0 ? 'error' : 'warn',
              text:
                `${executed} pushed, ${needsReview} need${needsReview === 1 ? 's' : ''} review, ` +
                `${failed} failed — see the rows below`,
            },
      );
    },
    onError: (err: Error) => setToast({ kind: 'error', text: err.message }),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.cancelQueuedChange(id),
    onSuccess: invalidate,
    onError: (err: Error) => setToast({ kind: 'error', text: err.message }),
  });

  const rebaselineMutation = useMutation({
    mutationFn: (id: string) => api.rebaselineQueuedChange(id),
    onSuccess: invalidate,
    onError: (err: Error) => setToast({ kind: 'error', text: err.message }),
  });

  // Resubmits the same target with confirmedLargeChange: true — the same
  // thing PATCH /api/queue/:id already does for a retarget, just without
  // changing the value. Puts the row back in the normal queue; it does not
  // push it — syncing stays a separate, deliberate step.
  const confirmMutation = useMutation({
    mutationFn: (entry: PendingChangeEntry) => api.modifyQueuedChange(entry.id, entry.newValue, true),
    onSuccess: invalidate,
    onError: (err: Error) => setToast({ kind: 'error', text: err.message }),
  });

  const confirmAllMutation = useMutation({
    mutationFn: () => api.confirmAllQueued(),
    onSuccess: (result) => {
      invalidate();
      setToast({ kind: 'ok', text: `${result.confirmed} change${result.confirmed === 1 ? '' : 's'} confirmed` });
    },
    onError: (err: Error) => setToast({ kind: 'error', text: err.message }),
  });

  // Covers the recheck itself, not just the queue fetch that follows it — the
  // query above stays disabled the whole time, so its own isLoading (v5:
  // isPending && isFetching) never turns true on its own during that wait.
  if ((canEdit && recheckMutation.isPending) || isLoading) {
    return <div className="empty-state">Loading…</div>;
  }
  if (error) return <div className="alert error">{(error as Error).message}</div>;

  const entries = data?.entries ?? [];
  const conflictEntries = entries.filter((e) => e.status === 'conflict');
  const needsConfirmationEntries = entries.filter((e) => e.status === 'needs_confirmation');
  const busy =
    cancelMutation.isPending ||
    rebaselineMutation.isPending ||
    executeMutation.isPending ||
    confirmMutation.isPending;

  return (
    <div>
      <div className="toolbar">
        <span className="muted small">
          {entries.length} staged change{entries.length === 1 ? '' : 's'}
          {/* Pushing the whole queue lives in the always-visible bar above
              every tab (PendingBar's "Sync Changes") — one button for it,
              reachable from wherever a change happens to be staged from,
              rather than a second one here that does the same thing. */}
        </span>
        <div className="spacer" />
        {canEdit && needsConfirmationEntries.length > 0 && (
          <button
            className="primary small"
            disabled={confirmAllMutation.isPending}
            onClick={() => confirmAllMutation.mutate()}
          >
            {confirmAllMutation.isPending ? 'Confirming…' : `Confirm all ${needsConfirmationEntries.length}`}
          </button>
        )}
      </div>

      {toast && (
        <div className={`alert ${toast.kind}`} onClick={() => setToast(null)} role="status">
          {toast.text}
        </div>
      )}

      {/* Two distinct situations, two distinct messages — a channel that
          actually moved is a different fact from a staged price that is
          simply large and unconfirmed, and conflating them under one
          "conflict" banner is what made this confusing before. */}
      {conflictEntries.length > 0 && (
        <div className="alert warn">
          {conflictEntries.length} staged change{conflictEntries.length === 1 ? '' : 's'} no longer{' '}
          {conflictEntries.length === 1 ? 'matches' : 'match'} what the channel currently shows — someone
          else's edit or a sync moved it. Pick{' '}
          <strong>Use current</strong> to keep your target price and treat the channel's new value as the
          baseline, or <strong>Cancel</strong> to drop the change.
        </div>
      )}
      {needsConfirmationEntries.length > 0 && (
        <div className="alert warn">
          {needsConfirmationEntries.length} staged price{needsConfirmationEntries.length === 1 ? '' : 's'}{' '}
          increase{needsConfirmationEntries.length === 1 ? 's' : ''} by more than the configured warning
          threshold and {needsConfirmationEntries.length === 1 ? "hasn't" : "haven't"} been confirmed — nothing
          on the channel has changed, the target itself is just large. Review and <strong>Confirm</strong> the
          ones you want to keep (or <strong>Confirm all</strong> above), or <strong>Cancel</strong> to drop
          one.
        </div>
      )}

      {entries.length === 0 ? (
        <div className="empty-state">
          <p>Nothing staged.</p>
          <p className="small">
            Edit a price, title or SKU on the Products tab — it lands here instead of publishing right
            away, so a batch of changes can go out together.
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="grid history pending-changes">
            <thead>
              <tr>
                <th>Staged</th>
                <th>Channel</th>
                <th>Product</th>
                <th className="align-right">Current</th>
                <th className="align-right">New</th>
                <th>By</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <Row
                  key={entry.id}
                  entry={entry}
                  canEdit={canEdit}
                  onCancel={() => cancelMutation.mutate(entry.id)}
                  onRebaseline={() => rebaselineMutation.mutate(entry.id)}
                  onConfirm={() => confirmMutation.mutate(entry)}
                  onExecuteOne={() => executeMutation.mutate([entry.id])}
                  busy={busy}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({
  entry,
  canEdit,
  onCancel,
  onRebaseline,
  onConfirm,
  onExecuteOne,
  busy,
}: {
  entry: PendingChangeEntry;
  canEdit: boolean;
  onCancel: () => void;
  onRebaseline: () => void;
  onConfirm: () => void;
  onExecuteOne: () => void;
  busy: boolean;
}) {
  // The channel's own drift is the only case where the staged baseline is
  // stale — everywhere else (including needs_confirmation, which never
  // touched the channel) baselineValue is still an accurate "current".
  const current = entry.status === 'conflict' ? (entry.conflictValue ?? entry.baselineValue) : entry.baselineValue;
  const isPrice = entry.field === 'price';

  return (
    <tr>
      <td className="small muted nowrap">{new Date(entry.createdAt).toLocaleString()}</td>
      <td className="nowrap">
        <ChannelTag name={entry.channelName} type={entry.channelType} />
      </td>
      <td className="title-cell">
        <div>{entry.productTitle ?? entry.listingTitle}</div>
        {entry.sku && <div className="sku">{entry.sku}</div>}
        <div className="muted small">{entry.field}</div>
      </td>
      <td className={`nowrap align-right${isPrice ? ' price-figure' : ''}`}>
        {formatChangeValue(entry.field, current, entry.currency)}
      </td>
      <td className={`nowrap align-right${isPrice ? ' price-figure' : ''}`}>
        <strong>{formatChangeValue(entry.field, entry.newValue, entry.currency)}</strong>
        {entry.status === 'needs_confirmation' && entry.lastError && (
          <div className="small warn-text">{entry.lastError}</div>
        )}
      </td>
      <td className="small nowrap">
        {entry.source === 'sheet' ? (
          <span title={entry.sourceRef ?? undefined}>Google Sheet</span>
        ) : entry.queuedBy ? (
          entry.queuedBy.name || entry.queuedBy.email
        ) : (
          <span className="muted">deleted user</span>
        )}
      </td>
      <td className="nowrap">
        <StatusBadge entry={entry} />
      </td>
      <td className="nowrap">
        {canEdit && entry.status !== 'executing' && (
          <div style={{ display: 'flex', gap: 6 }}>
            {entry.status === 'conflict' ? (
              <button className="link small" disabled={busy} onClick={onRebaseline}>
                Use current
              </button>
            ) : entry.status === 'needs_confirmation' ? (
              <button className="link small" disabled={busy} onClick={onConfirm}>
                Confirm
              </button>
            ) : (
              <button className="link small" disabled={busy} onClick={onExecuteOne}>
                Push now
              </button>
            )}
            <button className="link small danger" disabled={busy} onClick={onCancel}>
              Cancel
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function StatusBadge({ entry }: { entry: PendingChangeEntry }) {
  if (entry.status === 'executing') {
    return (
      <span className="badge" title="Being pushed right now">
        pushing…
      </span>
    );
  }
  if (entry.status === 'conflict') {
    return (
      <span className="badge warn" title="The channel's price has changed since this was staged">
        conflict
      </span>
    );
  }
  if (entry.status === 'needs_confirmation') {
    return (
      <span
        className="badge warn"
        title={entry.lastError ?? 'This price change is larger than usual and needs a second look'}
      >
        needs confirmation
      </span>
    );
  }
  if (entry.status === 'failed') {
    return (
      <span className="badge error" title={entry.lastError ?? undefined}>
        failed
      </span>
    );
  }
  return <span className="badge">queued</span>;
}
